/**
 * EntryModelLinkPickerModal Component
 *
 * Multi-select picker modal for linking entry models to a playbook.
 * Shows active entry models grouped by entry model category.
 * Pre-selects already-linked entry models (active only - archived linked items
 * won't appear since picker only shows active entry models).
 *
 * Archived linked entry models are preserved on Apply - they are merged back
 * into the selection to prevent silent data loss.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  Listbox,
  ListboxItem,
  ListboxSection,
} from '@enfomentis/ui';
import { entryModelService } from '@/services/EntryModelService';
import { entryModelCategoryService } from '@/services/EntryModelCategoryService';
import type { Selection } from '@react-types/shared';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';

export interface EntryModelLinkPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Currently selected entry model IDs (for pre-selection) */
  selectedEntryModelIds: string[];
  /** Handler for selection change - receives new array of selected IDs */
  onSelectionChange: (selectedIds: string[]) => void;
}

/**
 * Empty state component when no active entry models exist
 */
const EmptyState: React.FC = () => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <Layers className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Entry Models Available</h4>
      <p className="text-sm text-text-secondary">
        Create entry models in Reference Data to link them to your playbooks.
      </p>
    </div>
  </div>
);

export const EntryModelLinkPickerModal: React.FC<EntryModelLinkPickerModalProps> = ({
  isOpen,
  onClose,
  selectedEntryModelIds,
  onSelectionChange,
}) => {
  const navigate = useNavigate();

  // Local selection state - synced from props when modal opens
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(new Set());

  // Store original selected IDs to preserve archived entry models on Apply
  const originalSelectedIdsRef = useRef<string[]>([]);

  // Data fetching state
  const [activeEntryModels, setActiveEntryModels] = useState<EntryModel[]>([]);
  const [categories, setCategories] = useState<EntryModelCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync selection from props when modal opens
  // Filter to only IDs present in active entry models list
  useEffect(() => {
    if (isOpen) {
      // Store original selected IDs before filtering (to preserve archived on Apply)
      originalSelectedIdsRef.current = [...selectedEntryModelIds];

      // Load data when modal opens
      const loadData = async () => {
        setIsLoading(true);
        try {
          const [entryModelData, categoryData] = await Promise.all([
            entryModelService.getActiveEntryModels(),
            entryModelCategoryService.getAll(),
          ]);
          setActiveEntryModels(entryModelData);
          setCategories(categoryData);

          // Pre-select only IDs that exist in the active entry models list
          const activeIds = new Set(entryModelData.map((em) => em.id));
          const validSelection = selectedEntryModelIds.filter((id) => activeIds.has(id));
          setLocalSelectedKeys(new Set(validSelection));
        } catch (error) {
          console.error('Failed to load entry model data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadData();
    }
  }, [isOpen, selectedEntryModelIds]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, EntryModelCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Group entry models by category, sort categories alphabetically, uncategorized at bottom
  const { categorizedGroups, uncategorized } = useMemo(() => {
    const grouped = new Map<string, EntryModel[]>();
    const uncategorizedItems: EntryModel[] = [];

    activeEntryModels.forEach((item) => {
      if (item.entryModelCategoryId) {
        const existing = grouped.get(item.entryModelCategoryId) || [];
        grouped.set(item.entryModelCategoryId, [...existing, item]);
      } else {
        uncategorizedItems.push(item);
      }
    });

    // Sort each category's items alphabetically by name
    grouped.forEach((items, categoryId) => {
      grouped.set(
        categoryId,
        [...items].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
      );
    });

    // Sort uncategorized items alphabetically by name
    const sortedUncategorized = [...uncategorizedItems].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    // Sort categories alphabetically by name
    const sortedCategories = Array.from(grouped.entries())
      .map(([categoryId, items]) => {
        const category = categoryMap.get(categoryId);
        return category ? { category, items } : null;
      })
      .filter((g): g is { category: EntryModelCategory; items: EntryModel[] } => g !== null)
      .sort((a, b) =>
        a.category.name.localeCompare(b.category.name, undefined, { sensitivity: 'base' })
      );

    return {
      categorizedGroups: sortedCategories,
      uncategorized: sortedUncategorized,
    };
  }, [activeEntryModels, categoryMap]);

  /**
   * Handle selection change from Listbox
   * Guards against HeroUI Listbox emitting "all" for select-all scenarios
   */
  const handleSelectionChange = (keys: Selection): void => {
    // Guard against "all" sentinel before spreading
    if (keys === 'all') return;
    // Filter out any remaining non-string keys for safety
    const newKeys = new Set([...keys].filter((k): k is string => typeof k === 'string'));
    setLocalSelectedKeys(newKeys);
  };

  /**
   * Handle apply selection
   * Preserves archived linked entry models by merging them back in
   * De-dupes via Set to ensure no duplicate IDs
   */
  const handleApply = (): void => {
    const selectedActiveIds = Array.from(localSelectedKeys);

    // Only merge archived IDs if we successfully loaded entry model data
    // (prevents treating all IDs as archived on fetch failure)
    if (activeEntryModels.length > 0) {
      const activeIds = new Set(activeEntryModels.map((em) => em.id));
      const archivedIds = originalSelectedIdsRef.current.filter((id) => !activeIds.has(id));

      // Merge with de-dupe using Set
      const merged = Array.from(new Set([...selectedActiveIds, ...archivedIds]));
      onSelectionChange(merged);
    } else {
      // On fetch failure, preserve original selection unchanged (with de-dupe for consistency)
      onSelectionChange(Array.from(new Set(originalSelectedIdsRef.current)));
    }

    onClose();
  };

  /**
   * Handle cancel - just close, selection resets on next open
   */
  const handleCancel = (): void => {
    onClose();
  };

  const hasItems = activeEntryModels.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="lg"
      scrollBehavior="inside"
      placement="top-center"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <Layers className="w-5 h-5 text-primary" />
              <span>Select Entry Models</span>
            </ModalHeader>

            <ModalBody className="gap-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12 text-text-tertiary text-sm">
                  Loading...
                </div>
              ) : !hasItems ? (
                <EmptyState />
              ) : (
                <Listbox
                  aria-label="Entry model items"
                  selectionMode="multiple"
                  selectedKeys={localSelectedKeys}
                  onSelectionChange={handleSelectionChange}
                  variant="light"
                  classNames={{
                    base: 'max-h-[400px] overflow-auto',
                  }}
                >
                  {[
                    // Categorized items (sorted alphabetically by category name)
                    ...categorizedGroups.map(({ category, items }) => (
                      <ListboxSection
                        key={category.id}
                        title={category.name}
                        classNames={{
                          heading:
                            'text-sm font-semibold text-text-secondary flex w-full border-b border-default-200 pb-1',
                          base: 'mb-2',
                        }}
                      >
                        {items.map((item) => (
                          <ListboxItem
                            key={item.id}
                            textValue={item.name}
                            classNames={{
                              base: 'py-2 rounded-md my-1',
                              selectedIcon: 'text-primary scale-150 order-first',
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: category.color }}
                              />
                              <span className="font-medium text-text-primary">{item.name}</span>
                            </div>
                          </ListboxItem>
                        ))}
                      </ListboxSection>
                    )),

                    // Uncategorized items at bottom
                    ...(uncategorized.length > 0
                      ? [
                          <ListboxSection
                            key="uncategorized"
                            title="Uncategorized"
                            classNames={{
                              heading:
                                'text-sm font-semibold text-text-tertiary flex w-full border-b border-default-200 pb-1',
                              base: 'mb-2',
                            }}
                          >
                            {uncategorized.map((item) => (
                              <ListboxItem
                                key={item.id}
                                textValue={item.name}
                                classNames={{
                                  base: 'py-2 rounded-md my-1',
                                  selectedIcon: 'text-primary scale-150 order-first',
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-text-primary">{item.name}</span>
                                </div>
                              </ListboxItem>
                            ))}
                          </ListboxSection>,
                        ]
                      : []),
                  ]}
                </Listbox>
              )}
            </ModalBody>

            <ModalFooter className="justify-between">
              {/* Manage Entry Models shortcut */}
              <Link
                onPress={() => {
                  onClose();
                  navigate('/settings/strategies/reference-data?tab=entry-models');
                }}
                showAnchorIcon
                size="sm"
                className="cursor-pointer"
              >
                Manage Entry Models
              </Link>

              <div className="flex gap-2">
                <Button variant="light" onPress={handleCancel}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleApply}>
                  Apply
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
